<script>
import { mapGetters, mapActions } from 'vuex';
import DocumentsTitleUpload from '@/V2/documents/DocumentsTitleUpload';
import CustomerFraudUploading from '@/V2/customers/fraud/CustomerFraudUploading.vue';
import CustomerFraudSummary from '@/V2/customers/fraud/CustomerFraudSummary.vue';
import CustomerFraudDistribution from '@/V2/customers/fraud/CustomerFraudDistribution.vue';
import CustomerInsights from '@/V2/customers/overview/CustomerInsights.vue';
import CustomerFraudDocumentsFullList from '@/V2/customers/fraud/CustomerFraudDocumentsFullList.vue';
import { $event } from '@/bus';
import { getLatestAgentRun, hasAgentRunInProgress } from '@/utils/ai-agents';
import { getTopRiskLevel } from '@/constants/riskLevels';

export default {
  components: {
    DocumentsTitleUpload,
    CustomerFraudUploading,
    CustomerFraudSummary,
    CustomerFraudDistribution,
    CustomerInsights,
    CustomerFraudDocumentsFullList,
  },
  computed: {
    ...mapGetters([
      'getCachedCustomerReviewAgentRunsForCustomerUUID',
      'getCachedDocumentReviewAgentRunsForCustomerUUID',
      'someDocumentUploadingOrProcessing',
      'getCachedFraudSummary',
    ]),
    customerId() {
      return this.$route.params.customer_id;
    },
    showDocumentsUploadingComponent() {
      return this.someDocumentUploadingOrProcessing;
    },
    fraudSummary() {
      return this.getCachedFraudSummary(this.customerId);
    },
    fraudSummaryLoading() {
      return typeof this.fraudSummary === 'undefined';
    },
    latestCustomerReviewAgentRun() {
      const runs = this.getCachedCustomerReviewAgentRunsForCustomerUUID(
        this.customerId,
      );
      return getLatestAgentRun(runs);
    },
    customerHasDocuments() {
      return (
        this.fraudSummary?.aggregated_risk_counts &&
        Object.values(this.fraudSummary?.aggregated_risk_counts).some(
          (value) => value > 0,
        )
      );
    },
    anyCustomerReviewAgentIsRunning() {
      const runs = this.getCachedCustomerReviewAgentRunsForCustomerUUID(
        this.customerId,
      );
      return hasAgentRunInProgress(runs);
    },
    anyDocumentReviewAgentIsRunning() {
      const runs = this.getCachedDocumentReviewAgentRunsForCustomerUUID(
        this.customerId,
      );
      return hasAgentRunInProgress(runs);
    },
    agentIsRunning() {
      return (
        this.anyDocumentReviewAgentIsRunning ||
        this.anyCustomerReviewAgentIsRunning ||
        this.showDocumentsUploadingComponent
      );
    },
    riskLevel() {
      return getTopRiskLevel(this.fraudSummary);
    },
  },
  methods: {
    ...mapActions([
      'fetchAndCacheCustomerReviewAgentRunsForCustomerUUID',
      'fetchAndCacheDocumentReviewAgentRunsForCustomerUUID',
    ]),
    onUploadDocumentsClick() {
      const documentsTitleUpload = this.$refs.documentsTitleUpload;
      documentsTitleUpload.startUpload();
    },
  },
};
</script>

<template>
  <div
    class="risk-analysis i-px-11 i-pt-11 i-pb-11 i-flex"
    data-testid="risk-analysis-page"
  >
    <div class="risk-analysis-overview-container d-flex flex-column">
      <!-- title with upload -->
      <documents-title-upload
        ref="documentsTitleUpload"
        title="Risk Analysis"
        subtitle="View the analysis results for this customer"
        :expanded="false"
      >
      </documents-title-upload>

      <!-- content -->
      <div class="overview-content d-flex flex-column i-gap-8">
        <customer-fraud-uploading v-if="showDocumentsUploadingComponent" />

        <!-- first row of summaries -->
        <div class="d-flex flex-row i-gap-8">
          <!-- summary -->
          <customer-fraud-summary
            class="summary-card"
            :summary="fraudSummary"
            title="Analysis"
            :latest-customer-run="latestCustomerReviewAgentRun"
            :customer-has-documents="customerHasDocuments"
            :loading="fraudSummaryLoading"
            :agent-is-running="agentIsRunning"
            ai-label="AI Agent"
            data-testid="customer-risk-analysis-summary"
          />

          <!-- document distribution -->
          <customer-fraud-distribution
            class="summary-card"
            :loading="fraudSummaryLoading"
            :summary="fraudSummary"
            data-testid="customer-risk-analysis-documents"
          />
        </div>

        <!-- insights -->
        <customer-insights data-testid="customer-risk-analysis-insights" />

        <!-- documents -->
        <customer-fraud-documents-full-list
          :risk-level="riskLevel"
          :fraud-summary="fraudSummary"
          @upload-documents="onUploadDocumentsClick"
          data-testid="customer-risk-analysis-documents-full-list"
        />
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.risk-analysis {
  overflow-y: auto;

  .risk-analysis-overview-container {
    max-width: 1200px;
    margin: 0 auto;
  }

  .summary-card {
    width: 100%;

    :deep(.expandable-container),
    :deep(.content) {
      height: 100% !important;
    }
  }
}
</style>
